"use client";

import { useCallback, useEffect, useRef, useState } from "react";

import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Accordion as BaseAccordion,
} from "@unreserved/ui/components/accordion";

import type { FeatureItem } from "@/app/_libs/data/landing-page/features-section";
import { cn } from "@/lib/utils";

interface FeaturesAccordionProps {
  features: FeatureItem[];
  intervalMs?: number;
}

export function FeaturesAccordion({ features, intervalMs = 5000 }: FeaturesAccordionProps) {
  // Use index-based values to ensure valid ARIA IDs (no spaces or special chars)
  const getValue = (index: number) => `feature-${index}`;

  const [activeValue, setActiveValue] = useState<string | undefined>(
    features.length > 0 ? getValue(0) : undefined,
  );

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const scheduleNext = useCallback(
    (currentValue: string | undefined) => {
      if (!currentValue || features.length <= 1) return;

      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      timerRef.current = setTimeout(() => {
        // Extract index from value (e.g., "feature-0" -> 0)
        const currentIndex = parseInt(currentValue.replace("feature-", ""), 10);

        if (isNaN(currentIndex)) return;

        const nextIndex = (currentIndex + 1) % features.length;
        setActiveValue(getValue(nextIndex));
      }, intervalMs);
    },
    [features, intervalMs],
  );

  useEffect(() => {
    scheduleNext(activeValue);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [activeValue, scheduleNext]);

  const handleValueChange = (value: string | string[]) => {
    if (!value || Array.isArray(value)) return;

    setActiveValue(value);
  };

  if (!features.length) {
    return null;
  }

  return (
    <BaseAccordion
      type="single"
      collapsible={false}
      value={activeValue}
      onValueChange={handleValueChange}
      className="relative flex min-h-px w-full min-w-px flex-[1_0_0] flex-col items-start gap-5 md:h-[37vh] md:gap-6 lg:h-[43vh]">
      {features.map((feature, index) => {
        const value = getValue(index);

        return (
          <AccordionItem
            key={value}
            value={value}
            className={cn(
              "relative flex w-full cursor-pointer flex-col items-start gap-4 border-t-0 border-r-0 border-b-0 border-l-[3px] border-solid py-3 pr-0 pl-5 md:py-2",
              activeValue === value ? "border-typography-600" : feature.borderColor,
            )}>
            <AccordionTrigger className="relative flex w-full cursor-pointer flex-col items-start gap-3 p-0 text-left hover:no-underline [&>svg]:hidden">
              <p className="relative w-full text-sm leading-snug font-bold tracking-[0.2px] text-black md:text-sm lg:text-xl">
                {feature.title}
              </p>
            </AccordionTrigger>
            {feature.description && (
              <AccordionContent className="p-0">
                <p className="relative w-full cursor-pointer text-xs font-normal text-[#374151] md:text-xs lg:text-base">
                  {feature.description}
                </p>
              </AccordionContent>
            )}
          </AccordionItem>
        );
      })}
    </BaseAccordion>
  );
}
